import type { UseQueryOptions } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

type EntitlementData = {
	allowed: boolean;
	reason?: string;
};

interface UseEntitlementOptions {
	/**
	 * Query options from orpc.entitlements.checkFeature.queryOptions({...}).
	 * Each app constructs this from its own typed oRPC instance.
	 */
	queryOptions: UseQueryOptions<EntitlementData>;
	/** Whether the query should be enabled (e.g., when orgId is available). Defaults to true. */
	enabled?: boolean;
}

interface UseEntitlementResult {
	/** Whether the feature is allowed by the current plan */
	isAllowed: boolean;
	/** Reason for denial, if not allowed */
	reason: string | undefined;
	/** Whether the entitlement check is still loading */
	isLoading: boolean;
	/** Whether the query encountered an error */
	isError: boolean;
}

/**
 * Hook to check if an organization has access to a specific feature.
 *
 * @example
 * ```tsx
 * const { isAllowed, isLoading } = useEntitlement({
 *   queryOptions: orpc.entitlements.checkFeature.queryOptions({
 *     input: { organizationId: orgId, featureName: "tool:my-tool" },
 *   }),
 *   enabled: !!orgId,
 * });
 * ```
 */
export const useEntitlement = ({
	queryOptions,
	enabled = true,
}: UseEntitlementOptions): UseEntitlementResult => {
	const { data, isLoading, isError } = useQuery({
		...queryOptions,
		enabled,
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
	});

	return {
		isAllowed: data?.allowed ?? false,
		reason: data?.reason,
		isLoading,
		isError,
	};
};
